<template>
    <div class="sap-handle-stat-panel">
        <div class="form-inline mb-2">
            <label class="mr-1">{{ $t('label.sith.sap.sithMesSapHandleRecord.stat.from') }}</label>
            <input type="date" class="form-control form-control-sm mr-2" v-model="fromDate"/>
            <label class="mr-1">{{ $t('label.sith.sap.sithMesSapHandleRecord.stat.to') }}</label>
            <input type="date" class="form-control form-control-sm mr-2" v-model="toDate"/>

            <label class="mr-1">{{ $t('label.sith.sap.sithMesSapHandleRecord.stat.granularity') }}</label>
            <b-form-select class="mr-2" size="sm" style="width:110px"
                           v-model="granularity" :options="granularityOptions"></b-form-select>

            <label class="mr-1">{{ $t('label.sith.sap.sithMesSapHandleRecord.stat.flag') }}</label>
            <b-form-select class="mr-2" size="sm" style="width:110px"
                           v-model="flagScope" :options="flagOptions"></b-form-select>

            <b-button size="sm" variant="primary" :disabled="loading" @click="reload">
                <i class="fa fa-fw" :class="loading ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
                {{ $t('label.sith.sap.sithMesSapHandleRecord.stat.refresh') }}
            </b-button>
        </div>

        <div class="mb-2" v-if="allModules.length">
            <label class="mr-2">{{ $t('label.sith.sap.sithMesSapHandleRecord.stat.module') }}</label>
            <b-form-checkbox-group v-model="selectedModules" :options="allModules" size="sm"
                                   class="d-inline-block"></b-form-checkbox-group>
        </div>

        <b-alert variant="info" show v-if="coarsenedMessage">{{ coarsenedMessage }}</b-alert>
        <b-alert variant="warning" show v-if="backfillMessage">{{ backfillMessage }}</b-alert>
        <b-alert variant="warning" show v-if="stat.tailTruncated">
            {{ $t('label.sith.sap.sithMesSapHandleRecord.stat.tailTruncated') }}
        </b-alert>

        <b-overlay :show="loading" rounded="sm">
            <div v-if="!loading && !visibleBuckets.length" class="text-muted text-center py-4">
                {{ $t('label.sith.sap.sithMesSapHandleRecord.stat.empty') }}
            </div>
            <div v-show="visibleBuckets.length">
                <div ref="countChart" style="width:100%;height:320px"></div>
                <div ref="delayChart" style="width:100%;height:320px;margin-top:12px"></div>

                <h6 class="mt-3">{{ $t('label.sith.sap.sithMesSapHandleRecord.stat.summary') }}</h6>
                <b-table small bordered striped :items="summaryRows" :fields="summaryFields"></b-table>
            </div>
        </b-overlay>
    </div>
</template>

<script>
    // SAP 文件处理记录统计面板
    // 数据来自 GET /sithMesSapHandleRecord/stat (预聚合桶 + 实时尾部合并后的结果)
    //
    // 后端序列化的三个坑(见 plan 的 "(S2 discovered)" 约束), 本文件所有取值都按这三条处理:
    //  1. 请求参数用 yyyy-MM-dd'T'HH:mm:ss (T 分隔), 后端是 @DateTimeFormat(pattern=...) 绑定的;
    //  2. 响应里的时间是**字符串**且**空格分隔**("2026-08-18 00:00:00", jackson date-format),
    //     与请求的 T 分隔不对称, 且 new Date("2026-08-18 00:00:00") 在 Safari 上是 Invalid Date,
    //     所以一律走 parseServerTime() 手工解析, 不依赖 new Date(str);
    //  3. jackson default-property-inclusion=NON_NULL, avgDelaySec / maxDelaySec 为 null 时
    //     **整个 key 都不出现**, 所以判空一律用 toNumberOrNull() 而不是 === null。
    import * as echarts from 'echarts';

    const DAY_MS = 24 * 60 * 60 * 1000;
    const HOUR_MS = 60 * 60 * 1000;

    // 与后端 SithMesSapHandleStatServiceImpl.MAX_BUCKETS 对齐: 超过就会被服务端自动放粗粒度。
    // 这里只是提前把放不下的粒度置灰, 真正的闸门在服务端 (StatGranularity.coarsenToFit)。
    const MAX_BUCKETS = 1000;

    // "2026-08-18 00:00:00" / "2026-08-18T00:00:00" / "2026-08-18" 都能解析
    const SERVER_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/;

    function toDateInputValue(date) {
        const pad = n => (n < 10 ? '0' + n : '' + n);
        return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
    }

    // 后端时间统一在这里解析, 解析不了返回 null (调用方回落到原始字符串)
    function parseServerTime(value) {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        if (value instanceof Date) {
            return isNaN(value.getTime()) ? null : value;
        }
        if (typeof value === 'number') {
            return new Date(value);
        }
        const matched = SERVER_TIME_PATTERN.exec(String(value));
        if (!matched) {
            return null;
        }
        return new Date(Number(matched[1]), Number(matched[2]) - 1, Number(matched[3]),
            Number(matched[4] || 0), Number(matched[5] || 0), Number(matched[6] || 0));
    }

    function serverTimeMillis(value) {
        const parsed = parseServerTime(value);
        return parsed === null ? 0 : parsed.getTime();
    }

    // NON_NULL 会让 key 整个消失, 所以只认真正的数字
    function toNumberOrNull(value) {
        return typeof value === 'number' && !isNaN(value) ? value : null;
    }

    function toCount(value) {
        const num = toNumberOrNull(value);
        return num === null ? 0 : num;
    }

    // 估算 [from 00:00, to 23:59:59] 按某粒度会切出多少个桶, 口径跟服务端一致
    function estimateBucketCount(fromDate, toDate, granularity) {
        const from = parseServerTime(fromDate + 'T00:00:00');
        const to = parseServerTime(toDate + 'T23:59:59');
        if (from === null || to === null || to.getTime() <= from.getTime()) {
            return 0;
        }
        const span = to.getTime() - from.getTime();
        if (granularity === 'HOUR') {
            return Math.floor(span / HOUR_MS) + 1;
        }
        if (granularity === 'WEEK') {
            return Math.floor(span / (7 * DAY_MS)) + 1;
        }
        if (granularity === 'MONTH') {
            return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()) + 1;
        }
        return Math.floor(span / DAY_MS) + 1;
    }

    function emptyStat() {
        return {buckets: [], modules: [], tailTruncated: false, backfillDone: true};
    }

    export default {
        name: "sapHandleRecordStatPanel",
        data() {
            const today = new Date();
            const weekAgo = new Date(today.getTime() - 6 * DAY_MS);
            return {
                loading: false,
                fromDate: toDateInputValue(weekAgo),
                toDate: toDateInputValue(today),
                granularity: 'DAY',
                flagScope: 'ALL',
                selectedModules: [],
                // 已请求过的参数组合 -> 响应, 避免来回切换重复打后端
                cache: {},
                stat: emptyStat(),
                // 当前展示的这份 stat 是用哪个起始日请求的(回填提示要拿它跟 coveredFrom 比)
                statFromDate: null,
                countChart: null,
                delayChart: null
            };
        },
        computed: {
            granularityOptions() {
                // 跨度撑不下的粒度直接置灰: 选了也会被服务端放粗, 不如提前说清楚
                return ['HOUR', 'DAY', 'WEEK', 'MONTH'].map(value => ({
                    value: value,
                    text: this.granularityText(value),
                    disabled: estimateBucketCount(this.fromDate, this.toDate, value) > MAX_BUCKETS
                }));
            },
            // 服务端可能把粒度放粗(响应里的 granularity 才是真正用的那个),
            // 图表的时间轴格式必须跟着它走, 否则 UI 会谎报自己在画什么
            effectiveGranularity() {
                return this.stat.granularity || this.granularity;
            },
            coarsenedMessage() {
                // 加载中时 stat 还是上一次的结果, 跟新选的粒度比会误报
                if (this.loading || !this.stat.granularity || this.stat.granularity === this.granularity) {
                    return '';
                }
                return this.$t('label.sith.sap.sithMesSapHandleRecord.stat.granularityCoarsened',
                    {granularity: this.granularityText(this.stat.granularity)});
            },
            flagOptions() {
                const prefix = 'label.sith.sap.sithMesSapHandleRecord.stat.flag.';
                return [
                    {value: 'ALL', text: this.$t(prefix + 'all')},
                    {value: 'SUCCESS', text: this.$t(prefix + 'success')},
                    {value: 'ERROR', text: this.$t(prefix + 'error')}
                ];
            },
            allModules() {
                return this.stat.modules || [];
            },
            // module 未勾选时视为全选
            visibleBuckets() {
                const buckets = this.stat.buckets || [];
                if (!this.selectedModules.length) {
                    return buckets;
                }
                return buckets.filter(b => this.selectedModules.indexOf(b.module) >= 0);
            },
            // 验收 #9: 回填未完成 **且** 请求范围早于 coveredFrom 才提示
            // (只画 coveredFrom 之后的部分由后端裁剪保证: 持久桶取 [max(from, coveredFrom), min(to, coveredTo)))
            backfillMessage() {
                if (this.stat.backfillDone !== false || !this.stat.coveredFrom || !this.statFromDate) {
                    return '';
                }
                const coveredFrom = parseServerTime(this.stat.coveredFrom);
                const requestedFrom = parseServerTime(this.statFromDate);
                if (coveredFrom === null || requestedFrom === null
                    || requestedFrom.getTime() >= coveredFrom.getTime()) {
                    return '';
                }
                const progress = toNumberOrNull(this.stat.backfillProgress);
                const percent = Math.round((progress === null ? 0 : progress) * 100);
                return this.$t('label.sith.sap.sithMesSapHandleRecord.stat.backfilling',
                    {time: this.formatDateTime(this.stat.coveredFrom), percent: percent});
            },
            summaryFields() {
                const prefix = 'label.sith.sap.sithMesSapHandleRecord.stat.';
                return [
                    {key: 'module', label: this.$t(prefix + 'module')},
                    {key: 'total', label: this.$t(prefix + 'total')},
                    {key: 'success', label: this.$t(prefix + 'success')},
                    {key: 'error', label: this.$t(prefix + 'error')},
                    {key: 'errorRate', label: this.$t(prefix + 'errorRate')},
                    {key: 'delayCount', label: this.$t(prefix + 'delayCount')},
                    {key: 'avgDelay', label: this.$t(prefix + 'avgDelay')},
                    {key: 'maxDelay', label: this.$t(prefix + 'maxDelay')}
                ];
            },
            summaryRows() {
                const byModule = Object.create(null);
                this.visibleBuckets.forEach(b => {
                    let row = byModule[b.module];
                    if (!row) {
                        row = {module: b.module, success: 0, error: 0, delayCount: 0, delaySum: 0, maxDelaySec: null};
                        byModule[b.module] = row;
                    }
                    const delayCount = toCount(b.delayCount);
                    const avgDelaySec = toNumberOrNull(b.avgDelaySec);
                    const maxDelaySec = toNumberOrNull(b.maxDelaySec);
                    row.success += toCount(b.successCount);
                    row.error += toCount(b.errorCount);
                    row.delayCount += delayCount;
                    // 各桶的 avg 按各自的 delayCount 加权还原成 sum, 再整体求平均
                    if (avgDelaySec !== null) {
                        row.delaySum += avgDelaySec * delayCount;
                    }
                    if (maxDelaySec !== null && (row.maxDelaySec === null || maxDelaySec > row.maxDelaySec)) {
                        row.maxDelaySec = maxDelaySec;
                    }
                });
                return Object.keys(byModule).sort().map(module => {
                    const row = byModule[module];
                    const total = row.success + row.error;
                    return {
                        module: module,
                        total: total,
                        success: row.success,
                        error: row.error,
                        errorRate: total ? (row.error * 100 / total).toFixed(1) + '%' : '-',
                        delayCount: row.delayCount,
                        avgDelay: row.delayCount ? this.formatDuration(row.delaySum / row.delayCount) : '-',
                        maxDelay: row.maxDelaySec !== null ? this.formatDuration(row.maxDelaySec) : '-'
                    };
                });
            }
        },
        watch: {
            granularity() {
                this.fetchStat();
            },
            fromDate() {
                this.fetchStat();
            },
            toDate() {
                this.fetchStat();
            },
            // 验收 #3: flag 口径纯前端计算(VO 同时带 successCount 与 errorCount), 切换不打后端
            flagScope() {
                this.renderCharts();
            },
            selectedModules() {
                this.renderCharts();
            }
        },
        created() {
            // 请求序号(非响应式, 不参与渲染): 每发起一次统计请求就 +1,
            // 响应回来时号对不上就整份丢弃 —— 见 fetchStat 的注释
            this.$_statSeq = 0;
        },
        mounted() {
            this.fetchStat();
            window.addEventListener('resize', this.resizeCharts);
        },
        beforeDestroy() {
            // 组件都要没了, 在途响应一律作废, 免得回来往已销毁的实例上写 stat / 重建图表
            this.$_statSeq++;
            window.removeEventListener('resize', this.resizeCharts);
            if (this.countChart) {
                this.countChart.dispose();
                this.countChart = null;
            }
            if (this.delayChart) {
                this.delayChart.dispose();
                this.delayChart = null;
            }
        },
        methods: {
            cacheKey() {
                return [this.fromDate, this.toDate, this.granularity].join('|');
            },
            reload() {
                delete this.cache[this.cacheKey()];
                this.fetchStat();
            },
            applyStat(fromDate, data) {
                const stat = data || emptyStat();
                this.stat = stat;
                this.statFromDate = fromDate;
                // 新一批数据里可能不再有原来勾选的 module, 顺手剔掉, 否则图表会莫名其妙变空
                const modules = stat.modules || [];
                const kept = this.selectedModules.filter(m => modules.indexOf(m) >= 0);
                if (kept.length !== this.selectedModules.length) {
                    this.selectedModules = kept;
                }
                this.renderCharts();
            },
            fetchStat() {
                const key = this.cacheKey();
                const fromDate = this.fromDate;
                // 先占号再干活: 只有序号仍是最新的那次请求有资格回写 stat / cache / loading。
                // 后端没有取消通道(api/get 是裸 axios), 而连点粒度+日期或在加载中点刷新会同时派出多个请求,
                // 旧请求后到就会把新结果盖回去, 面板于是显示与自己控件不匹配的数据(如已经不该再出现的回填提示)。
                const seq = ++this.$_statSeq;
                if (this.cache[key]) {
                    // 命中缓存也已经占了新号, 在途的旧请求回来会被丢弃, 它的 finally 也就不再动 loading,
                    // 所以这里必须自己把 loading 复位, 否则遮罩会永久转圈
                    this.loading = false;
                    this.applyStat(fromDate, this.cache[key]);
                    return;
                }
                // 时间参数必须是 yyyy-MM-dd'T'HH:mm:ss, 后端不接受空格分隔也不接受 ISO 毫秒/时区
                const url = process.env.VUE_APP_SITHMES_PRD_URL
                    + '/sithMesSapHandleRecord/stat'
                    + '?fromTime=' + encodeURIComponent(fromDate + 'T00:00:00')
                    + '&toTime=' + encodeURIComponent(this.toDate + 'T23:59:59')
                    + '&granularity=' + encodeURIComponent(this.granularity);
                this.loading = true;
                // EmResponseUtils.success 是裸透传, api/get 拿到的就是 VO 本身, 不要再 .data 一层
                this.$store.dispatch("api/get", {url})
                    .then(data => {
                        if (seq !== this.$_statSeq) {
                            // 过期响应: 连 cache 都不写。reload() 刚删掉的 key 若被它补回去,
                            // "刷新" 绕缓存的语义就废了 —— 下次切回该组合又会读到旧快照
                            return;
                        }
                        this.cache[key] = data || emptyStat();
                        this.applyStat(fromDate, this.cache[key]);
                    })
                    .catch(() => {
                        // 错误提示由 api/get 的 commonErrorHandle 统一弹, 这里只把面板复位
                        if (seq === this.$_statSeq) {
                            this.applyStat(fromDate, emptyStat());
                        }
                    })
                    .finally(() => {
                        // 旧请求不能提前关掉遮罩(那会让刷新按钮在新请求还在飞的时候又可点);
                        // 最新的那次无论成败都会走到这里, 所以 loading 不会卡死
                        if (seq === this.$_statSeq) {
                            this.loading = false;
                        }
                    });
            },
            granularityText(value) {
                const prefix = 'label.sith.sap.sithMesSapHandleRecord.stat.granularity.';
                return this.$t(prefix + String(value).toLowerCase());
            },
            countOf(bucket) {
                if (this.flagScope === 'SUCCESS') {
                    return toCount(bucket.successCount);
                }
                if (this.flagScope === 'ERROR') {
                    return toCount(bucket.errorCount);
                }
                return toCount(bucket.successCount) + toCount(bucket.errorCount);
            },
            formatTime(value) {
                const date = parseServerTime(value);
                if (date === null) {
                    return value === null || value === undefined ? '' : String(value);
                }
                const pad = n => (n < 10 ? '0' + n : '' + n);
                const ymd = date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
                return this.effectiveGranularity === 'HOUR' ? ymd + ' ' + pad(date.getHours()) + ':00' : ymd;
            },
            formatDateTime(value) {
                const date = parseServerTime(value);
                if (date === null) {
                    return value === null || value === undefined ? '' : String(value);
                }
                const pad = n => (n < 10 ? '0' + n : '' + n);
                return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate())
                    + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes());
            },
            formatDuration(seconds) {
                const value = toNumberOrNull(seconds);
                if (value === null) {
                    return '-';
                }
                if (value < 60) {
                    return Math.round(value) + 's';
                }
                if (value < 3600) {
                    return (value / 60).toFixed(1) + 'min';
                }
                return (value / 3600).toFixed(1) + 'h';
            },
            buildAxis() {
                const times = [];
                const seenTime = Object.create(null);
                const modules = [];
                const seenModule = Object.create(null);
                this.visibleBuckets.forEach(b => {
                    const time = String(b.time);
                    if (!seenTime[time]) {
                        seenTime[time] = true;
                        times.push(b.time);
                    }
                    if (!seenModule[b.module]) {
                        seenModule[b.module] = true;
                        modules.push(b.module);
                    }
                });
                // 后端返回的是字符串时间, 不能丢给 new Date() 直接相减
                times.sort((a, b) => serverTimeMillis(a) - serverTimeMillis(b));
                modules.sort();
                return {times: times, modules: modules};
            },
            renderCharts() {
                this.$nextTick(() => {
                    if (!this.visibleBuckets.length) {
                        return;
                    }
                    const axis = this.buildAxis();
                    const times = axis.times;
                    const modules = axis.modules;
                    const labels = times.map(t => this.formatTime(t));
                    // 两级 map: module 名与时间串都可能含分隔符, 拼一个 key 有撞车风险
                    const index = Object.create(null);
                    this.visibleBuckets.forEach(b => {
                        let byTime = index[b.module];
                        if (!byTime) {
                            byTime = Object.create(null);
                            index[b.module] = byTime;
                        }
                        byTime[String(b.time)] = b;
                    });
                    const bucketAt = (module, time) => {
                        const byTime = index[module];
                        return byTime ? (byTime[String(time)] || null) : null;
                    };

                    // 图 1: 处理数量, 堆叠柱状图按 module 分系列
                    const countSeries = modules.map(module => ({
                        name: module,
                        type: 'bar',
                        stack: 'total',
                        data: times.map(t => {
                            const bucket = bucketAt(module, t);
                            return bucket ? this.countOf(bucket) : 0;
                        })
                    }));
                    this.countChart = this.ensureChart(this.countChart, this.$refs.countChart);
                    if (this.countChart) {
                        this.countChart.setOption({
                            title: {text: this.$t('label.sith.sap.sithMesSapHandleRecord.stat.countChart')},
                            tooltip: {trigger: 'axis', axisPointer: {type: 'shadow'}},
                            legend: {top: 28, type: 'scroll'},
                            grid: {top: 70, left: 50, right: 30, bottom: 60},
                            xAxis: {type: 'category', data: labels, axisLabel: {rotate: 30}},
                            yAxis: {type: 'value'},
                            series: countSeries
                        }, true);
                    }

                    // 图 2: delay。avg 走左轴, max 走右轴且默认关闭
                    // (max 与 avg 量级差距大, 同轴会把 avg 压成一条平线)
                    const avgLabel = this.$t('label.sith.sap.sithMesSapHandleRecord.stat.avgDelay');
                    const maxLabel = this.$t('label.sith.sap.sithMesSapHandleRecord.stat.maxDelay');
                    const delaySeries = [];
                    const legendSelected = {};
                    modules.forEach(module => {
                        delaySeries.push({
                            name: module + ' ' + avgLabel,
                            type: 'line',
                            yAxisIndex: 0,
                            connectNulls: true,
                            data: times.map(t => {
                                const bucket = bucketAt(module, t);
                                // avgDelaySec 为 null 时 key 根本不存在, toNumberOrNull 兜住
                                const avg = bucket ? toNumberOrNull(bucket.avgDelaySec) : null;
                                return avg === null ? null : Number(avg.toFixed(1));
                            })
                        });
                        const maxName = module + ' ' + maxLabel;
                        legendSelected[maxName] = false;
                        delaySeries.push({
                            name: maxName,
                            type: 'line',
                            yAxisIndex: 1,
                            lineStyle: {type: 'dashed'},
                            connectNulls: true,
                            data: times.map(t => {
                                const bucket = bucketAt(module, t);
                                return bucket ? toNumberOrNull(bucket.maxDelaySec) : null;
                            })
                        });
                    });
                    this.delayChart = this.ensureChart(this.delayChart, this.$refs.delayChart);
                    if (this.delayChart) {
                        this.delayChart.setOption({
                            title: {text: this.$t('label.sith.sap.sithMesSapHandleRecord.stat.delayChart')},
                            tooltip: {
                                trigger: 'axis',
                                valueFormatter: value => (value === null || value === undefined
                                    ? '-' : this.formatDuration(value))
                            },
                            legend: {top: 28, type: 'scroll', selected: legendSelected},
                            grid: {top: 70, left: 60, right: 60, bottom: 60},
                            xAxis: {type: 'category', data: labels, axisLabel: {rotate: 30}},
                            yAxis: [
                                {type: 'value', name: avgLabel},
                                {type: 'value', name: maxLabel}
                            ],
                            series: delaySeries
                        }, true);
                    }
                });
            },
            ensureChart(instance, el) {
                if (instance) {
                    instance.resize();
                    return instance;
                }
                // 面板在 b-collapse 里, 展开动画期间容器可能还没有尺寸, 拿不到就等下一次渲染
                if (!el || !el.offsetWidth) {
                    return null;
                }
                return echarts.init(el);
            },
            // 供列表页在 b-collapse 展开完成时调用: 折叠期间容器尺寸为 0, 展开后要补一次
            resizeCharts() {
                if (this.countChart === null || this.delayChart === null) {
                    // 还没建起来(多半是首次渲染时容器尺寸为 0), 这次补建
                    this.renderCharts();
                    return;
                }
                this.countChart.resize();
                this.delayChart.resize();
            }
        }
    };
</script>
